import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/ui/data-table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useToast } from '@/hooks/use-toast'
import { Plus, MoreHorizontal, Eye, Edit, Trash2, TestTube, Globe, Shield, Bell, BookOpen } from 'lucide-react'
import { format } from 'date-fns'
import { ColumnDef } from '@tanstack/react-table'

// Webhook配置表单验证
const webhookSchema = z.object({
  name: z.string().min(1, '请输入Webhook名称'),
  url: z.string().url('请输入有效的URL'),
  secret: z.string().min(1, '请输入Webhook密钥'),
  events: z.array(z.string()).min(1, '请至少选择一个事件类型'),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
})

type WebhookForm = z.infer<typeof webhookSchema>

// Webhook数据接口
interface Webhook {
  id: number
  name: string
  url: string
  secret: string
  events: string[]
  description?: string
  isActive: boolean
  lastTriggeredAt?: string
  successCount: number
  failureCount: number
  createdAt: string
  updatedAt: string
}

// 可用的webhook事件类型
const availableEvents = [
  { value: 'sms.sent', label: '短信发送成功', category: 'SMS' },
  { value: 'sms.delivered', label: '短信已送达', category: 'SMS' },
  { value: 'sms.failed', label: '短信发送失败', category: 'SMS' },
  { value: 'mo.received', label: '上行消息接收', category: 'MO' },
  { value: 'account.low_balance', label: '账户余额不足', category: 'ACCOUNT' },
  { value: 'account.recharged', label: '账户充值成功', category: 'ACCOUNT' },
  { value: 'template.approved', label: '模板审核通过', category: 'REVIEW' },
  { value: 'template.rejected', label: '模板审核拒绝', category: 'REVIEW' },
  { value: 'signature.approved', label: '签名审核通过', category: 'REVIEW' },
  { value: 'signature.rejected', label: '签名审核拒绝', category: 'REVIEW' },
]

export default function WebhookManagement() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false)
  const [isDocsDialogOpen, setIsDocsDialogOpen] = useState(false)
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isTesting, setIsTesting] = useState(false)

  const { toast } = useToast()

  // 创建Webhook表单
  const createForm = useForm<WebhookForm>({
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      events: ['sms.sent', 'sms.failed'],
      isActive: true,
    },
  })

  // 编辑Webhook表单
  const editForm = useForm<WebhookForm>({
    resolver: zodResolver(webhookSchema),
  })

  // 加载Webhook列表
  const loadWebhooks = async () => {
    setIsLoading(true)
    try {
      // 这里应该调用真实的API，暂时使用模拟数据
      const mockWebhooks: Webhook[] = [
        {
          id: 1,
          name: '生产环境Webhook',
          url: 'https://api.example.com/webhooks/sms',
          secret: 'prod-secret-123',
          events: ['sms.sent', 'sms.delivered', 'sms.failed'],
          description: '生产环境的短信状态回调',
          isActive: true,
          lastTriggeredAt: '2025-08-23T11:45:00',
          successCount: 1250,
          failureCount: 3,
          createdAt: '2025-08-20T10:00:00',
          updatedAt: '2025-08-23T11:45:00',
        },
        {
          id: 2,
          name: '测试环境Webhook',
          url: 'https://test.example.com/webhooks/sms',
          secret: 'test-secret-456',
          events: ['sms.sent', 'sms.failed'],
          description: '测试环境的短信状态回调',
          isActive: false,
          lastTriggeredAt: '2025-08-22T15:30:00',
          successCount: 89,
          failureCount: 12,
          createdAt: '2025-08-21T14:00:00',
          updatedAt: '2025-08-22T15:30:00',
        },
      ]
      setWebhooks(mockWebhooks)
    } catch (error) {
      toast({
        title: '加载失败',
        description: '无法加载Webhook列表',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  // 创建Webhook
  const handleCreateWebhook = async (data: WebhookForm) => {
    try {
      // 这里应该调用真实的API
      const newWebhook: Webhook = {
        id: Date.now(),
        name: data.name,
        url: data.url,
        secret: data.secret,
        events: data.events,
        description: data.description,
        isActive: data.isActive,
        successCount: 0,
        failureCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      
      setWebhooks(prev => [newWebhook, ...prev])
      setIsCreateDialogOpen(false)
      createForm.reset()
      
      toast({
        title: '创建成功',
        description: 'Webhook已创建',
      })
    } catch (error) {
      toast({
        title: '创建失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }

  // 更新Webhook状态
  const handleToggleStatus = async (id: number) => {
    try {
      setWebhooks(prev => prev.map(webhook => 
        webhook.id === id ? { ...webhook, isActive: !webhook.isActive } : webhook
      ))
      
      toast({
        title: '更新成功',
        description: 'Webhook状态已更新',
      })
    } catch (error) {
      toast({
        title: '更新失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }

  // 删除Webhook
  const handleDeleteWebhook = async (id: number) => {
    if (!confirm('确定要删除这个Webhook吗？删除后无法恢复。')) {
      return
    }

    try {
      setWebhooks(prev => prev.filter(webhook => webhook.id !== id))
      toast({
        title: '删除成功',
        description: 'Webhook已删除',
      })
    } catch (error) {
      toast({
        title: '删除失败',
        description: '请稍后重试',
        variant: 'destructive',
      })
    }
  }

  // 测试Webhook
  const handleTestWebhook = async (webhook: Webhook) => {
    setIsTesting(true)
    try {
      // 这里应该调用真实的测试API
      await new Promise(resolve => setTimeout(resolve, 2000)) // 模拟API调用
      
      toast({
        title: '测试成功',
        description: 'Webhook测试请求已发送',
      })
      setIsTestDialogOpen(false)
    } catch (error) {
      toast({
        title: '测试失败',
        description: '请检查Webhook配置',
        variant: 'destructive',
      })
    } finally {
      setIsTesting(false)
    }
  }

  // 表格列定义
  const columns: ColumnDef<Webhook>[] = [
    {
      accessorKey: 'name',
      header: '名称',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Globe className="h-4 w-4 text-blue-500" />
          <span>{row.getValue('name')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'url',
      header: 'URL',
      cell: ({ row }) => (
        <div className="max-w-xs truncate">
          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
            {row.getValue('url')}
          </code>
        </div>
      ),
    },
    {
      accessorKey: 'events',
      header: '事件类型',
      cell: ({ row }) => {
        const events = row.getValue('events') as string[]
        return (
          <div className="flex flex-wrap gap-1">
            {events.slice(0, 2).map((event, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {event}
              </Badge>
            ))}
            {events.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{events.length - 2}
              </Badge>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'isActive',
      header: '状态',
      cell: ({ row }) => {
        const isActive = row.getValue('isActive')
        return (
          <Badge variant={isActive ? 'success' : 'secondary'}>
            {isActive ? '启用' : '禁用'}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'successCount',
      header: '成功次数',
      cell: ({ row }) => (
        <span className="text-green-600 font-medium">{row.getValue('successCount')}</span>
      ),
    },
    {
      accessorKey: 'failureCount',
      header: '失败次数',
      cell: ({ row }) => (
        <span className="text-red-600 font-medium">{row.getValue('failureCount')}</span>
      ),
    },
    {
      accessorKey: 'lastTriggeredAt',
      header: '最后触发',
      cell: ({ row }) => {
        const lastTriggeredAt = row.getValue('lastTriggeredAt') as string
        return lastTriggeredAt ? format(new Date(lastTriggeredAt), 'MM-dd HH:mm') : '-'
      },
    },
    {
      accessorKey: 'createdAt',
      header: '创建时间',
      cell: ({ row }) => format(new Date(row.getValue('createdAt') as string), 'yyyy-MM-dd HH:mm'),
    },
    {
      id: 'actions',
      header: '操作',
      cell: ({ row }) => {
        const webhook = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setSelectedWebhook(webhook)
                setIsTestDialogOpen(true)
              }}>
                <TestTube className="mr-2 h-4 w-4" />
                测试Webhook
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setSelectedWebhook(webhook)
                editForm.reset({
                  name: webhook.name,
                  url: webhook.url,
                  secret: webhook.secret,
                  events: webhook.events,
                  description: webhook.description,
                  isActive: webhook.isActive,
                })
                setIsEditDialogOpen(true)
              }}>
                <Edit className="mr-2 h-4 w-4" />
                编辑
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleToggleStatus(webhook.id)}>
                <Shield className="mr-2 h-4 w-4" />
                {webhook.isActive ? '禁用' : '启用'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDeleteWebhook(webhook.id)}>
                <Trash2 className="mr-2 h-4 w-4" />
                删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  // 页面加载时获取数据
  useState(() => {
    loadWebhooks()
  })

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <PageHeader 
        title="Webhook管理"
        description="管理您的Webhook回调配置，接收实时事件通知"
      />

      {/* 功能说明 */}
      <Alert>
        <Bell className="h-4 w-4" />
        <AlertDescription>
          Webhook用于接收短信状态、账户变更等实时事件通知。请确保您的服务器能够正确处理POST请求。
        </AlertDescription>
      </Alert>

      {/* 操作栏 */}
              <div className="flex justify-between items-center">
          <div className="text-sm text-muted-foreground">
            共 {webhooks.length} 个Webhook配置
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => setIsDocsDialogOpen(true)}>
              <BookOpen className="mr-2 h-4 w-4" />
              事件文档
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              创建Webhook
            </Button>
          </div>
        </div>

      {/* Webhook列表 */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">加载中...</div>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={webhooks}
            />
          )}
        </CardContent>
      </Card>

      {/* 创建Webhook对话框 */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>创建Webhook</DialogTitle>
            <DialogDescription>
              创建一个新的Webhook配置用于接收事件通知
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={createForm.handleSubmit(handleCreateWebhook)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>名称 *</Label>
                <Input {...createForm.register('name')} placeholder="请输入Webhook名称" />
                {createForm.formState.errors.name && (
                  <p className="text-sm text-red-600">{createForm.formState.errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>状态</Label>
                <Select 
                  value={createForm.watch('isActive') ? 'true' : 'false'}
                  onValueChange={(value) => createForm.setValue('isActive', value === 'true')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">启用</SelectItem>
                    <SelectItem value="false">禁用</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Webhook URL *</Label>
              <Input {...createForm.register('url')} placeholder="https://your-domain.com/webhook" />
              {createForm.formState.errors.url && (
                <p className="text-sm text-red-600">{createForm.formState.errors.url.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Webhook密钥 *</Label>
              <Input {...createForm.register('secret')} placeholder="用于验证webhook请求的密钥" />
              {createForm.formState.errors.secret && (
                <p className="text-sm text-red-600">{createForm.formState.errors.secret.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>描述</Label>
              <Textarea {...createForm.register('description')} placeholder="可选描述信息" />
            </div>

            <div className="space-y-2">
              <Label>事件类型 *</Label>
              <div className="space-y-4">
                {['SMS', 'MO', 'ACCOUNT', 'REVIEW'].map(category => (
                  <div key={category} className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {category === 'SMS' && '短信事件'}
                      {category === 'MO' && '上行消息事件'}
                      {category === 'ACCOUNT' && '账户事件'}
                      {category === 'REVIEW' && '审核事件'}
                    </h4>
                    <div className="grid grid-cols-2 gap-2 ml-4">
                      {availableEvents
                        .filter(event => event.category === category)
                        .map(event => (
                          <label key={event.value} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              value={event.value}
                              {...createForm.register('events')}
                            />
                            <span className="text-sm">{event.label}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
              {createForm.formState.errors.events && (
                <p className="text-sm text-red-600">{createForm.formState.errors.events.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">创建</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 测试Webhook对话框 */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>测试Webhook</DialogTitle>
            <DialogDescription>
              发送测试请求到Webhook URL以验证配置是否正确
            </DialogDescription>
          </DialogHeader>
          
          {selectedWebhook && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Webhook名称</Label>
                <p className="text-sm text-muted-foreground">{selectedWebhook.name}</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">目标URL</Label>
                <p className="text-sm text-muted-foreground break-all">{selectedWebhook.url}</p>
              </div>
              
              <Alert>
                <TestTube className="h-4 w-4" />
                <AlertDescription>
                  测试请求将包含示例数据，用于验证您的服务器是否能正确处理Webhook请求。
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsTestDialogOpen(false)}>
              取消
            </Button>
            <Button 
              onClick={() => selectedWebhook && handleTestWebhook(selectedWebhook)}
              disabled={isTesting}
            >
              {isTesting ? '测试中...' : '发送测试请求'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 事件文档对话框 */}
      <Dialog open={isDocsDialogOpen} onOpenChange={setIsDocsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <BookOpen className="mr-2 h-5 w-5" />
              Webhook事件文档
            </DialogTitle>
            <DialogDescription>
              了解所有支持的事件类型和集成指南
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* 事件类型分类 */}
            <div>
              <h3 className="text-lg font-semibold mb-4">事件类型分类</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">📱 短信事件</CardTitle>
                    <CardDescription>与短信发送状态相关的事件</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1 text-sm">
                      <li>• sms.sent - 短信发送成功</li>
                      <li>• sms.delivered - 短信已送达</li>
                      <li>• sms.failed - 短信发送失败</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">📨 上行消息事件</CardTitle>
                    <CardDescription>与用户回复短信相关的事件</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1 text-sm">
                      <li>• mo.received - 上行消息接收</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">💰 账户事件</CardTitle>
                    <CardDescription>与账户余额和充值相关的事件</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1 text-sm">
                      <li>• account.low_balance - 账户余额不足</li>
                      <li>• account.recharged - 账户充值成功</li>
                    </ul>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">✅ 审核事件</CardTitle>
                    <CardDescription>与模板和签名审核相关的事件</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-1 text-sm">
                      <li>• template.approved - 模板审核通过</li>
                      <li>• template.rejected - 模板审核拒绝</li>
                      <li>• signature.approved - 签名审核通过</li>
                      <li>• signature.rejected - 签名审核拒绝</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* 集成指南 */}
            <div>
              <h3 className="text-lg font-semibold mb-4">集成指南</h3>
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">安全说明</h4>
                  <p className="text-sm text-blue-800 mb-2">Webhook请求包含以下安全头信息：</p>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• X-Timestamp: 请求时间戳</li>
                    <li>• X-Signature: HMAC-SHA256签名</li>
                    <li>• Content-Type: application/json</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">集成步骤</h4>
                  <ol className="text-sm text-gray-800 space-y-2">
                    <li>1. 在Webhook管理页面创建新的Webhook配置，设置接收URL和密钥</li>
                    <li>2. 根据业务需求选择需要接收的事件类型</li>
                    <li>3. 使用HMAC-SHA256算法验证Webhook请求的签名</li>
                    <li>4. 根据事件类型处理相应的业务逻辑</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* 示例数据 */}
            <div>
              <h3 className="text-lg font-semibold mb-4">事件示例</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm mb-2">上行消息事件 (mo.received)</h4>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`{
  "event": "mo.received",
  "timestamp": "2025-08-23T10:00:00",
  "tenantId": 1222,
  "webhookId": 1,
  "data": {
    "moMessageId": "MO_123",
    "mobile": "13800138000",
    "content": "回复内容",
    "operator": "MOBILE",
    "province": "北京",
    "city": "北京"
  }
}`}
                  </pre>
                </div>
                
                <div>
                  <h4 className="font-medium text-sm mb-2">短信发送成功事件 (sms.sent)</h4>
                  <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`{
  "event": "sms.sent",
  "timestamp": "2025-08-23T10:00:00",
  "tenantId": 1222,
  "webhookId": 1,
  "data": {
    "messageId": "MSG_123",
    "mobile": "13800138000",
    "content": "验证码：123456",
    "sentAt": "2025-08-23T10:00:00"
  }
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setIsDocsDialogOpen(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
